/**
 * Текст.
 *
 * Меняется чаще всех остальных: заголовок правят прямо в эфире,
 * по букве. Поэтому здесь особенно важно, что `update` пишет
 * в существующий узел — пересоздание сбрасывало бы выделение
 * и позицию курсора, если этот же виджет показывают в редакторе
 * панели.
 *
 * Текст ставится через textContent, а не innerHTML. Это не мелочь:
 * содержимое приходит от модератора, а оверлей работает без присмотра
 * во время эфира — разметка оттуда исполнилась бы у всех зрителей,
 * и заметить это было бы некому.
 */
import { familyFor, registerFont, whenFontsReady } from './fonts.js';
import { asRecord, color, freeText, numberInRange, oneOf, optionalString, optionalURL, } from './props.js';
const TYPE = 'text';
const ALIGNMENTS = ['left', 'center', 'right'];
const WEIGHTS = ['normal', 'bold'];
/** Предел длины имени семейства. */
export const MAX_FAMILY = 64;
/**
 * Потолок длины надписи.
 *
 * Строка приходит из сети и уходит в DOM оверлея, который работает
 * без присмотра во время эфира. Десять тысяч знаков — это заведомо
 * больше любой надписи на кадре и заведомо меньше кадра протокола.
 */
export const MAX_TEXT = 10_000;
/** Разбирает оформление надписи. Общее для `text` и `timer`. */
export function parseTextStyle(type, props) {
    return {
        // Верхняя граница — высота кадра 4K: буква больше кадра
        // это уже не текст, а способ закрыть весь эфир одним слоем
        size: numberInRange(type, props, 'size', 1, 4320, 48),
        color: color(type, props, 'color', '#ffffff'),
        align: oneOf(type, props, 'align', ALIGNMENTS, 'left'),
        weight: oneOf(type, props, 'weight', WEIGHTS, 'normal'),
        // Имя семейства попадает в CSS, поэтому длина ограничена:
        // строка на килобайт в `font-family` — это не шрифт,
        // а попытка что-то сломать
        font: optionalString(type, props, 'font', MAX_FAMILY),
        fontSrc: optionalURL(type, props, 'fontSrc'),
    };
}
/** Переносит оформление в узел. Общее для `text` и `timer`. */
export function applyTextStyle(el, style) {
    /* eslint-disable no-param-reassign --
       Правило бережёт неизменяемость СОСТОЯНИЯ, а здесь меняется DOM —
       ровно то, ради чего функция и существует. */
    el.style.fontSize = `${style.size}px`;
    // Своё семейство появляется здесь только после готовности
    // шрифтов — решает это familyFor, и решает одно на все слои
    el.style.fontFamily = familyFor(style.font);
    el.style.color = style.color;
    el.style.textAlign = style.align;
    el.style.fontWeight = style.weight;
    // Перенос по словам, а не по буквам: разорванное посреди слова
    // название читается как ошибка
    el.style.overflowWrap = 'break-word';
    /* eslint-enable no-param-reassign */
}
export const textWidget = {
    type: TYPE,
    parse(raw) {
        const props = asRecord(TYPE, raw);
        return {
            ...parseTextStyle(TYPE, props),
            /*
              ПУСТОЙ ТЕКСТ РАЗРЕШЁН, И ЭТО ИСПРАВЛЕНИЕ, А НЕ ПОСЛАБЛЕНИЕ.
      
              Здесь стоял requiredString с доводом: пустой слой невидим,
              но занимает место в списке и ловит мышь. Довод верный
              и не про то. Он говорит о слое, КОТОРЫЙ ТАК ОСТАВИЛИ, —
              а запрет бил по слою, который правят прямо сейчас: стерев
              последнюю букву, человек получал не пустую надпись, а разбор
              со ссылкой на ошибку. Виджет отказывался обновиться, и
              в эфире оставалось прежнее слово — стереть надпись до конца
              было нельзя вовсе.
      
              Пустой слой при этом не теряется: в панели у него есть
              и строка списка, и рамка выделения, а невидим он ровно там,
              где и должен быть невидим, — у зрителя.
            */
            text: freeText(TYPE, props, 'text', MAX_TEXT),
        };
    },
    mount(props) {
        const el = document.createElement('div');
        el.dataset.widget = TYPE;
        const span = document.createElement('span');
        el.append(span);
        let current = props;
        registerFont({ family: props.font, src: props.fontSrc });
        apply(el, span, current);
        // Перерисовка по готовности шрифтов, а не ожидание перед
        // созданием: слой обязан появиться сразу, просто сперва
        // запасным шрифтом. Ждать здесь значило бы держать сцену
        // пустой, пока грузится файл
        const unwatch = whenFontsReady(() => {
            apply(el, span, current);
        });
        return {
            el,
            update(next) {
                current = next;
                registerFont({ family: next.font, src: next.fontSrc });
                apply(el, span, next);
            },
            destroy() {
                unwatch();
                el.remove();
            },
        };
    },
    /**
     * Заводит шрифт, не создавая слоя.
     *
     * Ради этого прогрев и нужен тексту: файл шрифта весит сотни
     * килобайт, и качать его в тот момент, когда надпись выводят
     * в кадр, значит показать её запасным шрифтом — то есть ровно то,
     * от чего защищает весь этот модуль.
     */
    preload(props) {
        registerFont({ family: props.font, src: props.fontSrc });
    },
};
function apply(el, span, props) {
    /* eslint-disable no-param-reassign --
       Правило бережёт неизменяемость СОСТОЯНИЯ, а здесь меняется DOM —
       ровно то, ради чего функция и существует. Требование
       иммутабельности относится к сцене, а не к узлам, которыми
       её рисуют. Тот же случай, что в apps/overlay/src/render.ts. */
    // textContent, а не innerHTML: см. комментарий к модулю
    if (span.textContent !== props.text) {
        span.textContent = props.text;
    }
    /* eslint-enable no-param-reassign */
    applyTextStyle(el, props);
}
//# sourceMappingURL=text.js.map